import { useCallback, useEffect, useState } from 'react';
import { SelectChangeEvent } from '@mui/material';
import { useTranslation, Trans } from 'react-i18next';
import styled from '@emotion/styled';
import { FormikProps } from 'formik';
import {
  StyledText,
  StyledFormGroup,
  StyledInput,
  StyledButton,
  StyledSelect,
  StyledCheckboxFormControl,
} from '@selfserve/react-ui';
import {
  links,
  companySizes,
  buttonIds,
  jobTitles,
} from '@selfserve/react-utils';
import { FormData } from '../utils/create-account';
import {
  useCountryList,
  useStorage,
  storage,
  storageKeys,
} from '@selfserve/react-hooks';
import { PasswordFields } from '@selfserve/react-ui';
import { PackageDetail } from '../data';

export const StyledInputFieldContainer = styled(StyledFormGroup)(
  ({
    theme,
    column,
    pair,
    verticalInMobile,
  }: {
    theme?: any;
    column?: boolean;
    pair?: boolean;
    verticalInMobile?: boolean;
  }) => ({
    position: 'relative',
    ...(column && {
      flexDirection: 'column',
      '> div': {
        flexDirection: 'row-reverse',
        justifyContent: 'flex-end',
      },
    }),
    ...(pair && {
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      gap: '24px',
      marginBottom: '24px',
      [theme.breakpoints.down('sm')]: {
        flexDirection: 'row',
        marginBottom: '24px',
      },
      '> div': {
        flexDirection: 'column',
        justifyContent: 'flex-end',
        flex: 1,
        marginBottom: 0,
        width: 'calc(50% - 12px)',
        marginRight: '0px !important',
      },
      ...(verticalInMobile && {
        [theme.breakpoints.down('sm')]: {
          flexDirection: 'column',
          marginBottom: '24px',
          '> div': {
            width: '100%',
          },
        },
      }),
    }),
    label: {
      marginBottom: '8px',
      marginLeft: '0',
      color: '#333',
      fontFamily: 'Source Sans Pro',
      p: {
        position: 'absolute',
        bottom: '-8px',
      },
    },
  })
);

const StyledSelectMenu = styled(StyledSelect)(
  ({ error }: { error?: boolean }) => ({
    fontWeight: 400,
    letterSpacing: '0.00938em',
    color: '#333',
    position: 'relative',
    cursor: 'text',
    display: 'inline-flex',
    alignItems: 'center',
    backgroundColor: '#fff',
    border: '1px solid #ccc',
    padding: '8px',
    paddingLeft: 0,
    paddingRight: 0,
    boxSizing: 'border-box',
    borderRadius: '4px',
    lineHeight: '19px',
    height: '40px',
    ...(error && { borderColor: 'rgba(225, 36, 55, 1) !important' }),
  })
);

const StyledCheckbox = styled(StyledCheckboxFormControl)(() => ({
  '+ span p': {
    color: '#000',
    fontFamily: 'Source Sans Pro',
    marginTop: 0,
    position: 'static',
  },
}));

const StyledSubmitBtn = styled(StyledButton)(() => ({
  minWidth: '158px',
}));

const StyledBtnInfo = styled(StyledText)(({ theme }: { theme?: any }) => ({
  marginBottom: '24px',
  fontFamily: 'Source Sans Pro',
  color: '#000',
  a: {
    color: theme.palette.primary.main,
    fontFamily: 'Source Sans Pro',
  },
}));

const StyledLink = styled('a')(() => ({
  fontWeight: '600',
  fontFamily: 'Source Sans Pro',
}));

interface Props {
  formik: FormikProps<FormData>;
  isLoading: boolean;
  isUserExist: boolean;
  isFullForm: boolean;
  packageData: PackageDetail;
}

export const CreateAccountForm = ({
  formik,
  isLoading,
  isUserExist,
  isFullForm,
  packageData,
}: Props) => {
  const {
    handleSubmit,
    handleChange,
    handleBlur,
    setFieldValue,
    values,
    errors,
    touched,
    setFieldTouched,
    isSubmitting,
    isValid,
    submitCount,
  } = formik;
  const { t } = useTranslation();
  const { password } = values;
  const [, setCountryCode] = useStorage(
    storage.SESSION,
    storageKeys.COUNTRY_CODE
  );
  const [showRMDCheckbox, setShowRMDCheckbox] = useState(false);
  const { countries, GDPRCountryCodes } = useCountryList();
  const {
    department_list: packageDeptList,
    company_size_list: packageCompanySizes,
  } = packageData;
  const selectedPackageDeptList =
    packageDeptList && packageDeptList.length > 0 ? packageDeptList : jobTitles;
  const selectedPackageCompanySizes =
    packageCompanySizes && packageCompanySizes.length > 0
      ? packageCompanySizes
      : companySizes;

  const handleCountryChange = useCallback(
    (e: SelectChangeEvent<unknown>) => {
      const countryCode = e.target.value;
      const isGDPR = GDPRCountryCodes.includes(countryCode as string);
      setShowRMDCheckbox(isGDPR);
      setFieldValue('allowReceivingMarketingData', !isGDPR);
      setFieldValue('countryCode', countryCode);
      setCountryCode(countryCode);
    },
    [setFieldValue, setShowRMDCheckbox, GDPRCountryCodes]
  );

  const handleCompanySizeChange = useCallback(
    (e: SelectChangeEvent<unknown>) =>
      setFieldValue('companySize', e.target.value),
    [setFieldValue]
  );

  const handleJobTitleChange = useCallback(
    (e: SelectChangeEvent<unknown>) =>
      setFieldValue('jobTitle', e.target.value),
    [setFieldValue]
  );

  useEffect(() => {
    if (password) {
      setFieldTouched('password', true);
    }
  }, [password, setFieldTouched]);

  useEffect(() => {
    if (!isValid && submitCount !== 0 && isSubmitting) {
      const firstErrorKey = Object.keys(errors)[0];
      if (document.getElementsByName(firstErrorKey).length) {
        document.getElementsByName(firstErrorKey)[0].focus();
      }
    }
  }, [submitCount, isValid, errors, isSubmitting]);

  const userFullNameField = () => (
    <>
      <StyledInput
        id="firstname"
        name="firstName"
        inputLabel={t('create-account.first-name')}
        onChange={handleChange}
        onBlur={handleBlur}
        value={values.firstName}
        error={!!touched.firstName && !!errors.firstName}
        formHelperText={touched.firstName ? errors.firstName : undefined}
        inputProps={{ 'aria-label': t('aria.first-name') }}
        autoFocus
      />
      <StyledInput
        id="lastname"
        name="lastName"
        inputLabel={t('create-account.last-name')}
        onChange={handleChange}
        onBlur={handleBlur}
        value={values.lastName}
        error={!!touched.lastName && !!errors.lastName}
        formHelperText={touched.lastName ? errors.lastName : undefined}
        inputProps={{ 'aria-label': t('aria.last-name') }}
      />
    </>
  );

  const userCompanyField = () => (
    <StyledInput
      id="company"
      name="company"
      inputLabel={t('create-account.company')}
      onChange={handleChange}
      onBlur={handleBlur}
      value={values.company}
      error={!!touched.company && !!errors.company}
      formHelperText={touched.company ? errors.company : undefined}
    />
  );

  const userCountryField = () =>
    isFullForm && (
      <StyledInputFieldContainer column>
        <label>{t('common.country')}</label>
        <StyledSelectMenu
          native
          labelId="countryCode"
          name="countryCode"
          className="p-dropdown"
          error={!!touched.countryCode && !!errors.countryCode}
          onChange={handleCountryChange}
          onBlur={handleBlur}
          defaultValue=""
          inputProps={{
            'aria-label': t('aria.country-code'),
            name: 'countryCode',
            id: 'country',
          }}
        >
          <option value="" disabled style={{ display: 'none' }}>
            {t('common.select')}
          </option>
          {countries.map((country) => (
            <option key={country.value} value={country.value}>
              {country.text}
            </option>
          ))}
        </StyledSelectMenu>
        {!!touched.countryCode && !!errors.countryCode && (
          <StyledText>{t('common.validations.country-required')}</StyledText>
        )}
      </StyledInputFieldContainer>
    );

  const userCompanySizeField = () =>
    isFullForm && (
      <StyledInputFieldContainer column withoutMargin>
        <label>{t('create-account.company-size')}</label>
        <StyledSelectMenu
          native
          labelId="companySize"
          name="companySize"
          className="p-dropdown"
          onChange={handleCompanySizeChange}
          onBlur={handleBlur}
          defaultValue=""
          inputProps={{
            'aria-label': t('aria.company-size'),
            name: 'companySize',
            id: 'companySize',
          }}
        >
          <option value="" disabled style={{ display: 'none' }}>
            {t('common.select')}
          </option>
          {selectedPackageCompanySizes.map((size, index) => (
            <option key={index} value={size}>
              {size}
            </option>
          ))}
        </StyledSelectMenu>
        {!!touched.companySize && !!errors.companySize && (
          <StyledText>
            {t('common.validations.company-size-required')}
          </StyledText>
        )}
      </StyledInputFieldContainer>
    );

  const userJobTitleField = () => (
    <StyledInputFieldContainer column withoutMargin>
      <label>{t('create-account.job-title')}</label>
      <StyledSelectMenu
        native
        labelId="jobTitle"
        name="jobTitle"
        className="p-dropdown"
        onChange={handleJobTitleChange}
        onBlur={handleBlur}
        defaultValue=""
        fullWidth
        inputProps={{
          'aria-label': t('aria.job-title'),
          name: 'jobTitle',
          id: 'jobTitle',
        }}
      >
        <option value="" disabled style={{ display: 'none' }}>
          {t('common.select')}
        </option>
        {selectedPackageDeptList.map((job, index) => (
          <option key={index} value={job}>
            {job}
          </option>
        ))}
      </StyledSelectMenu>
      {!!touched.jobTitle && !!errors.jobTitle && (
        <StyledText>{t('common.validations.job-title-required')}</StyledText>
      )}
    </StyledInputFieldContainer>
  );

  const userRMDCheckboxField = () => (
    <StyledInputFieldContainer>
      <label htmlFor="marketing" />
      <StyledCheckbox
        id="marketing"
        checked={values.allowReceivingMarketingData}
        label={t('create-account.allow-reciving-marketing-data')}
        name="allowReceivingMarketingData"
        onClick={handleChange}
        className="recive-marketing-data-checkbox"
        value={values.allowReceivingMarketingData}
      />
    </StyledInputFieldContainer>
  );

  const userTimezoneField = () => (
    <input
      id="timezone"
      name="timezone"
      type="hidden"
      value={values.timezone}
    />
  );

  const submitButtonContainer = () => (
    <>
      <div className="login-container recaptcha-checkbox-checkmark">
        <StyledBtnInfo>
          <Trans
            defaults="By clicking Create Account, you accept the terms of the <serviceAgreementLink>
            Service Agreement</serviceAgreementLink> and the <privacyPolicyLink>Privacy Policy</privacyPolicyLink>"
            components={{
              serviceAgreementLink: (
                <StyledLink href={links.SERVICE_AGREEMENT} target="_blank" />
              ),
              privacyPolicyLink: (
                <StyledLink href={links.PRIVACY_POLICY} target="_blank" />
              ),
            }}
          />
        </StyledBtnInfo>
      </div>
      <StyledSubmitBtn
        className="continue-button primary-button"
        type="submit"
        id={buttonIds.createAccount}
        loading={isLoading}
        aria-label="create account"
        onMouseDown={(event) => {
          //we need this to prevent double click in case if we have focused input
          event.preventDefault();
        }}
      >
        {t('create-account.create-account')}
      </StyledSubmitBtn>
    </>
  );

  return (
    <form onSubmit={handleSubmit}>
      <StyledInputFieldContainer row pair>
        {userFullNameField()}
      </StyledInputFieldContainer>
      <StyledInputFieldContainer row pair verticalInMobile>
        {userCompanyField()}
        {isFullForm && userCountryField()}
        {!isFullForm && userJobTitleField()}
      </StyledInputFieldContainer>

      {isFullForm && (
        <StyledInputFieldContainer>
          {userJobTitleField()}
        </StyledInputFieldContainer>
      )}
      {isFullForm && (
        <StyledInputFieldContainer>
          {userCompanySizeField()}
          {userTimezoneField()}
        </StyledInputFieldContainer>
      )}
      {!isUserExist && (
        <PasswordFields formik={formik} withoutConfirmation={!isFullForm} />
      )}
      {showRMDCheckbox && userRMDCheckboxField()}
      {submitButtonContainer()}
    </form>
  );
};
